import { log } from "../../../";

import * as React from "react";
import { findDOMNode } from "react-dom";
import { action, observable } from "mobx";
import { observer } from "mobx-react";

// Material UI
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Tooltip from "@material-ui/core/Tooltip";
import LinearProgress from "@material-ui/core/LinearProgress";
import { Fab } from "@material-ui/core";

// Custom UI
import { Colors } from "../../../core/utils/Colors";
import * as Icons from "../../../core/utils/Icons";
import { SearchBox } from "../../../components/SearchBox";
import { FetchList } from "./FetchList";
import { UploadImageDropZone } from "./UploadImageDropZone";
import Breadcrumbs from "../../Breadcrumbs";

// Store
import { CreateListsStore } from "../../../stores/CreateListsStore";

// Models
import * as CustomLists from "../../../models/customLists";

// Utils
import { WindowResizeChangeDispatcher } from "core/utils/WindowResizeChangeDispatcher";
import { DynamicTypographyType, DynamicTypography } from "core/utils/DynamicTypography";

// Localization
import { strings } from "../../../core/localizedStrings";

// Layout
import { Col, Row } from "layout";

export interface ImagesSelectorProps extends React.Props<any> {
    store: CreateListsStore;
    shouldShow: boolean;
    onSelectionComplete: () => void;
}

@observer
export class ImagesSelector extends React.Component<ImagesSelectorProps> {
    private lastScrollHeight: number = 0;

    @observable
    private listViewHeight: number = 400;

    constructor(props: ImagesSelectorProps) {
        super(props);
        this.props.store.handleTabChange(this.props.store.currentTab);
    }

    public componentDidMount() {
        WindowResizeChangeDispatcher.getInstance().addListener(this.onWindowResize);
    }

    public componentWillUnmount() {
        WindowResizeChangeDispatcher.getInstance().removeListener(this.onWindowResize);
    }

    @action
    private onWindowResize = (windowWidth: number, windowHeight: number) => {
        var listNode = findDOMNode(document.getElementById("imagesContainer")) as Element;
        if (listNode) {
            var listBoundingRect = listNode.getBoundingClientRect();
            this.listViewHeight = windowHeight - listBoundingRect.top - 190;
        }
    };
    // Render Methods
    public render() {
        log.verbose("Rendering Images Selector Dialog");

        return (
            <>
                {this.renderInstructions()}
                {this.renderTabs()}
                {this.renderSearchBox()}
                {this.renderListViewToolbar()}
                {this.renderDropzone()}
                {this.renderListView()}
            </>
        );
    }

    private onSearchTextChange = (text: string) => {
        this.props.store.search(text);
    };

    // Mobx Actions
    // ------------------------------------------
    @action
    private onEditFolderTitleClicked = (folder: CustomLists.Folder) => {
        this.props.store.folderToEdit = folder;
    };

    @action
    private onEditImageCardTitleClicked = (image: CustomLists.CategoryImage) => {
        this.props.store.itemToEdit = image;
    };

    @action
    private onAddFolderButtonClicked = (event: any) => {
        if (this.props.store.currentTab === CustomLists.TabSelection.SITE_IMAGES) {
            this.props.store.handleTabChange(CustomLists.TabSelection.MY_IMAGES);
        }
        this.props.store.folderToEdit = new CustomLists.Folder();
    };

    @action
    private onUploadImagesClicked = () => {
        if (this.props.store.currentTab === CustomLists.TabSelection.SITE_IMAGES) {
            this.props.store.handleTabChange(CustomLists.TabSelection.MY_IMAGES);
        }
        this.props.store.shouldShowImagesUploader = !this.props.store.shouldShowImagesUploader;
    };

    private handleTabChange = (event: any, value: any) => {
        this.props.store.handleTabChange(value);
    };

    private handleScroll = (event: any) => {
        let node = event.target;
        let atTop = node.scrollTop < 500;
        let atBottom = node.scrollHeight < node.scrollTop + window.innerHeight;
        if (atBottom && !atTop && this.lastScrollHeight !== node.scrollHeight) {
            this.props.store.checkShouldFetchMore();
            this.lastScrollHeight = node.scrollHeight;
        }
    };

    private renderInstructions() {
        return (
            <div style={{ margin: "3px 10px 10px 10px" }}>
                <Row>
                    <DynamicTypography variant={DynamicTypographyType.H4}>{strings.imageBank}</DynamicTypography>
                </Row>
                <Row>
                    <DynamicTypography variant={DynamicTypographyType.Body}>
                        {strings.imagesSelectorInstructions}
                    </DynamicTypography>
                </Row>
            </div>
        );
    }

    private renderSearchBox() {
        return (
            <Row style={{ width: "100%", padding: "5px 10px" }}>
                <SearchBox
                    initialValue={this.props.store.searchText}
                    hasBackground={false}
                    fontSize={18}
                    fontColor={Colors.black}
                    showLoading={this.props.store.isSearchQuerying}
                    onSearchTextChange={this.onSearchTextChange}
                />
            </Row>
        );
    }

    private renderTabs() {
        let siteLabel = strings.siteImages;
        let myImagesLabel = strings.myImages;

        if (this.props.store.isSearching) {
            let siteCount = this.props.store.siteListsSearchCount;
            let myImagesCount = this.props.store.myListsSearchCount;

            if (siteCount > 0) {
                siteLabel += " (" + siteCount + ")";
            }

            if (myImagesCount > 0) {
                myImagesLabel += " (" + myImagesCount + ")";
            }
        }

        return (
            <Row center="xs">
                <Tabs
                    indicatorColor="secondary"
                    textColor="secondary"
                    centered={true}
                    variant="fullWidth"
                    style={{ width: "95%", height: 20 }}
                    onChange={this.handleTabChange}
                    value={this.props.store.currentTab}
                >
                    <Tab label={siteLabel} value={CustomLists.TabSelection.SITE_IMAGES} />
                    <Tab label={myImagesLabel} value={CustomLists.TabSelection.MY_IMAGES} />
                </Tabs>
            </Row>
        );
    }

    private renderDropzone() {
        if (this.props.store.shouldShowImagesUploader) {
            return (
                <Row middle="xs" style={{ padding: 15 }}>
                    <UploadImageDropZone store={this.props.store} />
                </Row>
            );
        } else {
            return null;
        }
    }

    private renderListView() {
        return (
            <Col xs={12} id="imagesContainer" style={{ padding: 3 }}>
                {this.props.store.isSearching ? null : (
                    <Row style={{ padding: "0px 5px 5px 5px" }}>
                        <Breadcrumbs store={this.props.store} fontColor={Colors.black} />
                    </Row>
                )}
                {this.props.store.isLoading && this.props.store.visibleItems.length === 0 && (
                    <LinearProgress color="primary" />
                )}
                <Row>
                    <div
                        onScroll={this.handleScroll}
                        id="list"
                        style={{
                            overflowY: "auto",
                            overflowX: "hidden",
                            height: this.listViewHeight,
                            width: "100%",
                        }}
                    >
                        <FetchList
                            store={this.props.store}
                            selectedItems={this.props.store.categoryItemsToCreate}
                            editFolderCardTitleCallback={this.onEditFolderTitleClicked}
                            editImageCardTitleCallback={this.onEditImageCardTitleClicked}
                        />
                    </div>
                </Row>
                {this.props.store.isLoading && this.props.store.visibleItems.length === 0 && (
                    <LinearProgress color="primary" />
                )}
            </Col>
        );
    }

    private renderListViewToolbar() {
        let iconStyle = {
            width: 35,
            height: 35,
            marginRight: 5,
        };

        return (
            <Row end="xs" style={{ width: "100%", paddingTop: 3 }}>
                <Tooltip title={strings.uploadImages} placement="bottom">
                    <Fab color="primary" onClick={this.onUploadImagesClicked} style={iconStyle}>
                        <Icons.Upload color="secondary" />
                    </Fab>
                </Tooltip>
                <Tooltip title={strings.addFolder} placement="bottom">
                    <Fab
                        disabled={this.props.store.folderToEdit !== undefined}
                        color="primary"
                        onClick={this.onAddFolderButtonClicked}
                        style={iconStyle}
                    >
                        <Icons.AddFolder color="secondary" />
                    </Fab>
                </Tooltip>
            </Row>
        );
    }
}
